// src/pages/Admin/FormationDirigeants/TrainingArticleFormModal.jsx
import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/Modal/Modal";

import {
  FaSave,
  FaTimes,
  FaInfoCircle,
  FaLayerGroup,
  FaSortNumericUp,
  FaExternalLinkAlt,
} from "react-icons/fa";

const EMPTY = {
  title: "",
  subtitle: "",
  content: "",
  category: "DIRIGEANTS", // DIRIGEANTS | CERTIF_ASSO
  video_url: "",
  powerpoint_url: "",
  sort_order: 0,
  is_published: false,
};

export default function TrainingArticleFormModal({
  isOpen,
  onClose,
  initialData,
  onSubmit,
}) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const data = initialData || EMPTY;

    setForm({
      ...EMPTY,
      ...data,
      sort_order: Number(data?.sort_order || 0),
      is_published: Boolean(data?.is_published ?? false),
    });
  }, [initialData, isOpen]);

  const onChange = (e) => {
    const { name, type, checked, value } = e.target;
    setForm((s) => ({
      ...s,
      [name]:
        type === "checkbox"
          ? checked
          : name === "sort_order"
          ? Number(value || 0)
          : value,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title?.trim()) return;

    setSaving(true);
    try {
      const payload = {
        ...form,
        title: form.title?.trim(),
        subtitle: form.subtitle?.trim() || null,
        content: form.content || "",
        category: form.category || "DIRIGEANTS",
        video_url: form.video_url?.trim() || null,
        powerpoint_url: form.powerpoint_url?.trim() || null,
        sort_order: Number(form.sort_order || 0),
        is_published: Boolean(form.is_published),
      };
      await onSubmit(payload);
    } finally {
      setSaving(false);
    }
  };

  // ✅ plus de useMemo : calcul simple (pas de hook)
  const isEdit = Boolean(initialData?.id);
  const computedInfos = {
    photosCount: [
      initialData?.photo_url_1,
      initialData?.photo_url_2,
      initialData?.photo_url_3,
    ].filter(Boolean).length,
    hasVideo: Boolean(initialData?.video_url),
    hasPpt: Boolean(initialData?.powerpoint_url),
  };

  // ✅ le return conditionnel est OK tant qu'il est APRÈS tous les hooks (ici aucun hook en-dessous)
  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      labelledById="training-article-form-title"
    >
      <ModalHeader
        id="training-article-form-title"
        title={isEdit ? "Modifier un article" : "Créer un article"}
        subtitle="Formation des dirigeants / Certif Asso"
        onClose={onClose}
      />

      <ModalBody>
        <div className="grid md:grid-cols-3 gap-0 max-h-[70vh]">
          {/* Form (2/3) */}
          <div className="md:col-span-2 pr-1 overflow-y-auto">
            <form onSubmit={submit} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className="text-xs uppercase text-slate-500">
                    Titre *
                  </label>
                  <input
                    name="title"
                    value={form.title}
                    onChange={onChange}
                    required
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                    placeholder="Ex : Dossier d’affiliation, responsabilités..."
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="text-xs uppercase text-slate-500">
                    Sous-titre
                  </label>
                  <input
                    name="subtitle"
                    value={form.subtitle || ""}
                    onChange={onChange}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                    placeholder="Ex : Les points clés à connaître"
                  />
                </div>

                <div>
                  <label className="text-xs uppercase text-slate-500">
                    Catégorie
                  </label>
                  <select
                    name="category"
                    value={form.category}
                    onChange={onChange}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 bg-white"
                  >
                    <option value="DIRIGEANTS">Dirigeants</option>
                    <option value="CERTIF_ASSO">Certif Asso</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs uppercase text-slate-500">
                    Ordre d’affichage
                  </label>
                  <input
                    type="number"
                    name="sort_order"
                    value={form.sort_order}
                    onChange={onChange}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                    min={0}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="text-xs uppercase text-slate-500">
                    Contenu
                  </label>
                  <textarea
                    name="content"
                    value={form.content || ""}
                    onChange={onChange}
                    rows={10}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                    placeholder="Texte libre (HTML possible)."
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="text-xs uppercase text-slate-500">
                    Lien vidéo
                  </label>
                  <input
                    name="video_url"
                    value={form.video_url || ""}
                    onChange={onChange}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                    placeholder="https://..."
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="text-xs uppercase text-slate-500">
                    Lien PowerPoint
                  </label>
                  <input
                    name="powerpoint_url"
                    value={form.powerpoint_url || ""}
                    onChange={onChange}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                    placeholder="https://..."
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      name="is_published"
                      checked={Boolean(form.is_published)}
                      onChange={onChange}
                    />
                    <span>Publié (visible sur la page publique)</span>
                  </label>

                  {!isEdit ? (
                    <div className="mt-1 text-xs text-slate-500">
                      Astuce : après création, tu pourras uploader jusqu’à 3 photos via la liste (boutons 1/2/3).
                    </div>
                  ) : null}
                </div>
              </div>

              <ModalFooter>
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-slate-100 text-slate-800 hover:bg-slate-200"
                >
                  <FaTimes /> Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"
                >
                  <FaSave />{" "}
                  {saving ? "Enregistrement…" : isEdit ? "Enregistrer" : "Créer"}
                </button>
              </ModalFooter>
            </form>
          </div>

          {/* Aside (1/3) */}
          <aside className="md:col-span-1 pl-3 overflow-y-auto border-l border-slate-200">
            <div className="rounded-xl overflow-hidden ring-1 ring-slate-200 bg-white">
              <div className="px-4 py-3 border-b border-slate-100">
                <div className="font-semibold flex items-center gap-2">
                  <FaInfoCircle className="opacity-70" />
                  Aperçu / Infos
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Résumé rapide de l’article
                </div>
              </div>

              <div className="p-4 text-sm text-slate-700 space-y-3">
                <div className="flex items-center gap-2">
                  <FaLayerGroup className="opacity-70" />
                  <span className="truncate">
                    Catégorie :{" "}
                    <strong>
                      {form.category === "CERTIF_ASSO"
                        ? "Certif Asso"
                        : "Dirigeants"}
                    </strong>
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <FaSortNumericUp className="opacity-70" />
                  <span className="truncate">
                    Ordre : <strong>{Number(form.sort_order || 0)}</strong>
                  </span>
                </div>

                {isEdit ? (
                  <div className="rounded-lg bg-slate-50 ring-1 ring-slate-200 p-3 space-y-1">
                    <div className="text-xs uppercase opacity-70">Médias</div>
                    <div>
                      Photos : <strong>{computedInfos.photosCount}/3</strong>
                    </div>
                    <div>
                      Vidéo : <strong>{computedInfos.hasVideo ? "Oui" : "Non"}</strong>
                    </div>
                    <div>
                      PPT : <strong>{computedInfos.hasPpt ? "Oui" : "Non"}</strong>
                    </div>
                  </div>
                ) : null}

                {form.video_url ? (
                  <a
                    href={form.video_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-indigo-600 hover:underline"
                  >
                    <FaExternalLinkAlt className="text-[12px]" />
                    Ouvrir la vidéo
                  </a>
                ) : null}

                {form.powerpoint_url ? (
                  <a
                    href={form.powerpoint_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-indigo-600 hover:underline"
                  >
                    <FaExternalLinkAlt className="text-[12px]" />
                    Ouvrir le PowerPoint
                  </a>
                ) : null}
              </div>
            </div>
          </aside>
        </div>
      </ModalBody>
    </Modal>
  );
}
